import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL, getAuthHeader } from '@/lib/utils';
import ImageUpload from '@/components/ImageUpload';
import { Plus, Pencil, Trash2, GraduationCap, X } from 'lucide-react';

const AdminAcademicPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [featureInput, setFeatureInput] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', features: [], image_url: '',
    button_text: '', button_link: '', is_active: true
  });

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/academic-partners`, { headers: getAuthHeader() });
      setPartners(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPartners(); }, []);

  const resetForm = () => {
    setForm({ title: '', description: '', features: [], image_url: '', button_text: '', button_link: '', is_active: true });
    setEditing(null);
    setFeatureInput('');
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, description: p.description || '', features: p.features || [],
      image_url: p.image_url || '', button_text: p.button_text || '',
      button_link: p.button_link || '', is_active: p.is_active
    });
    setDialogOpen(true);
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm(prev => ({ ...prev, features: [...prev.features, featureInput.trim()] }));
      setFeatureInput('');
    }
  };

  const removeFeature = (i) => {
    setForm(prev => ({ ...prev, features: prev.features.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    try {
      if (editing) {
        await axios.put(`${API_URL}/academic-partners/${editing.id}`, form, { headers: getAuthHeader() });
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/academic-partners`, form, { headers: getAuthHeader() });
        toast.success('Created');
      }
      setDialogOpen(false);
      resetForm();
      fetchPartners();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await axios.delete(`${API_URL}/academic-partners/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchPartners();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="admin-academic-partners">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2a5e] font-manrope">Academic Partners</h1>
          <p className="text-gray-500 text-sm mt-1">Manage academic partnership sections shown on the homepage</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1a2a5e] hover:bg-[#2d4a8e]" data-testid="add-academic-btn">
              <Plus size={16} className="mr-2" /> Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Partner' : 'Add Academic Partner'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Approved Training Partner of Medhavi Skill University & Skill India" data-testid="academic-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="We are proud to be an Approved Training Partner..." rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Photo</Label>
                <ImageUpload value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} label="Partnership Photo (e.g. MOU signing ceremony)" />
              </div>
              <div className="space-y-2">
                <Label>Highlights / Features</Label>
                <div className="flex gap-2">
                  <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} placeholder="e.g. UGC Recognized Degrees" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }} />
                  <Button type="button" onClick={addFeature} variant="outline" size="sm">Add</Button>
                </div>
                <div className="space-y-1 mt-2">
                  {form.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded text-sm">
                      <span className="flex-1">{f}</span>
                      <button type="button" onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Button Text</Label>
                  <Input value={form.button_text} onChange={(e) => setForm({...form, button_text: e.target.value})} placeholder="Visit SIHMT Portal" />
                </div>
                <div className="space-y-2">
                  <Label>Button Link</Label>
                  <Input value={form.button_link} onChange={(e) => setForm({...form, button_link: e.target.value})} placeholder="https://www.sihmt.in" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})} />
                <Label>Active</Label>
              </div>
              <Button type="submit" className="w-full bg-[#1a2a5e] hover:bg-[#2d4a8e]" data-testid="save-academic-btn">
                {editing ? 'Update' : 'Add Partner'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : partners.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">No academic partners yet. Click "Add Partner" to get started.</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {partners.map((p) => (
            <Card key={p.id} data-testid={`academic-item-${p.id}`}>
              <CardContent className="flex items-center gap-4 py-4">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.title} className="w-16 h-16 rounded-lg object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center">
                    <GraduationCap size={24} className="text-[#1a2a5e]" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1a2a5e]">{p.title}</h3>
                  <p className="text-sm text-gray-500">{p.features?.length || 0} highlights | {p.button_text || 'No button'}</p>
                  {!p.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(p)}><Pencil size={14} /></Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(p.id)}><Trash2 size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAcademicPartners;
